import {useEffect, useRef} from 'react';
import type {MatrixCall} from 'matrix-js-sdk/lib/webrtc/call';
import type {Room} from 'livekit-client';
import {Track, type LocalVideoTrack} from 'livekit-client';
import {setMatrixCallCamera} from '../../../services/matrixCallService';
import {groupCallService} from '../../groupCalls/services/groupCallService';
import {createLivekitReliefAdapter} from './livekitReliefAdapter';
import {startLongTaskMonitor} from './longTaskMonitor';
import {createMatrixReliefAdapter} from './matrixReliefAdapter';
import {NO_EVIDENCE, groupEvidence, type ReliefEvidence} from './reliefSignals';
import {StatsDeltaTracker, analyzeStats, statsEntries} from './statsDelta';
import {VideoReliefController, type VideoReliefAdapter} from './videoReliefController';
import {createReliefLog, type ReliefSurface} from './videoReliefLog';
import {registerRelief, releaseRelief} from './videoReliefStore';

const POLL_MS = 2_000;

type EvidenceReader = (paused: boolean, cpuShare: number | undefined) => Promise<ReliefEvidence>;

const useReliefLoop = (
    surface: ReliefSurface,
    scope: string,
    enabled: boolean,
    resetKey: unknown,
    createAdapter: () => VideoReliefAdapter,
    createReader: () => EvidenceReader,
    liveRef: {readonly current: boolean},
): void => {
    const adapterRef = useRef(createAdapter);
    const readerRef = useRef(createReader);
    adapterRef.current = createAdapter;
    readerRef.current = createReader;
    useEffect(() => {
        if (!enabled) {
            return;
        }
        const controller = new VideoReliefController({adapter: adapterRef.current(), log: createReliefLog(surface)});
        const read = readerRef.current();
        const monitor = startLongTaskMonitor();
        registerRelief(scope, controller);
        let inFlight = false;
        const tick = () => {
            if (inFlight) {
                return;
            }
            inFlight = true;
            const evidence = liveRef.current ? read(controller.holding, monitor.share()) : Promise.resolve(NO_EVIDENCE);
            void evidence
                .catch(() => NO_EVIDENCE)
                .then(result => controller.evaluate(result))
                .finally(() => {
                    inFlight = false;
                });
        };
        const timer = window.setInterval(tick, POLL_MS);
        return () => {
            window.clearInterval(timer);
            monitor.stop();
            releaseRelief(scope, controller);
            controller.dispose();
        };
    }, [enabled, resetKey, surface, scope, liveRef]);
};

export const useMatrixVideoRelief = (scope: string, call: MatrixCall | null, enabled: boolean, live: boolean): void => {
    const liveRef = useRef(live);
    liveRef.current = live;
    useReliefLoop(
        'matrix',
        scope,
        enabled && !!call,
        call,
        () => createMatrixReliefAdapter({getCall: () => call, setCamera: setMatrixCallCamera}),
        () => {
            const tracker = new StatsDeltaTracker();
            return async (paused, cpuShare) => {
                const connection = call?.peerConn;
                if (!connection) {
                    return NO_EVIDENCE;
                }
                const verdict = analyzeStats(statsEntries(await connection.getStats()), tracker, paused);
                return {
                    networkDegraded: verdict.degraded === 'network',
                    networkGood: verdict.known && verdict.degraded === null,
                    cpuLimited: verdict.degraded === 'cpu',
                    cpuShare,
                };
            };
        },
        liveRef,
    );
};

export const useGroupVideoRelief = (scope: string, room: Room | undefined, enabled: boolean, quality: string | undefined, live: boolean): void => {
    const liveRef = useRef(live);
    const qualityRef = useRef(quality);
    liveRef.current = live;
    qualityRef.current = quality;
    useReliefLoop(
        'group',
        scope,
        enabled && !!room,
        room,
        () => createLivekitReliefAdapter({getRoom: () => room, setCamera: on => groupCallService.setCamera(on)}),
        () => async (paused, cpuShare) => {
            const camera = room?.localParticipant.getTrackPublication(Track.Source.Camera)?.track as LocalVideoTrack | undefined;
            const stats = !paused && camera ? await camera.getSenderStats().catch(() => []) : [];
            return groupEvidence(qualityRef.current, stats, paused, cpuShare);
        },
        liveRef,
    );
};
